/**
 * DXF Viewer Component
 * 
 * Rendert DXF (2D CAD) Dateien in einem Canvas/SVG Element.
 * Features:
 * - Zoom/Pan mit Touch und Maus
 * - Unterstützt Linien, Kreise, Bögen, Polylinien, Text
 * - Layer-Anzeige
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DxfParser from 'dxf-parser';
import { 
  ZoomIn, ZoomOut, Maximize2, Layers, Eye, EyeOff, 
  RotateCcw, Move, Download, X, Loader2, AlertCircle
} from 'lucide-react';

const DxfViewer = ({ fileUrl, fileName, onClose }) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  const [dxfData, setDxfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [layers, setLayers] = useState([]);
  const [visibleLayers, setVisibleLayers] = useState(new Set());
  const [showLayers, setShowLayers] = useState(false);
  
  // View state
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [bounds, setBounds] = useState(null);
  
  // Interaction state
  const [isDragging, setIsDragging] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  // Load and parse DXF file
  useEffect(() => {
    const loadDxf = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error(t('dxf.loadError', 'File could not be loaded'));
        
        const text = await response.text();
        const parser = new DxfParser();
        const dxf = parser.parseSync(text);
        
        if (!dxf) throw new Error(t('dxf.parseError', 'DXF file could not be parsed'));
        
        setDxfData(dxf);
        
        // Extract layers
        const layerNames = new Set();
        if (dxf.entities) {
          dxf.entities.forEach(entity => {
            if (entity.layer) layerNames.add(entity.layer);
          });
        }
        const layerList = Array.from(layerNames).sort();
        setLayers(layerList);
        setVisibleLayers(new Set(layerList));
        
        // Calculate bounds
        const b = calculateBounds(dxf.entities || []);
        setBounds(b);
        
      } catch (err) {
        console.error('DXF load error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadDxf();
  }, [fileUrl]);

  // Calculate bounds of all entities
  const calculateBounds = (entities) => {
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
    
    const updateBounds = (x, y) => {
      if (x !== undefined && y !== undefined && isFinite(x) && isFinite(y)) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    };
    
    entities.forEach(entity => {
      switch (entity.type) {
        case 'LINE':
          updateBounds(entity.vertices?.[0]?.x, entity.vertices?.[0]?.y);
          updateBounds(entity.vertices?.[1]?.x, entity.vertices?.[1]?.y);
          break;
        case 'CIRCLE':
          if (entity.center && entity.radius) {
            updateBounds(entity.center.x - entity.radius, entity.center.y - entity.radius);
            updateBounds(entity.center.x + entity.radius, entity.center.y + entity.radius);
          }
          break;
        case 'ARC':
          if (entity.center && entity.radius) {
            updateBounds(entity.center.x - entity.radius, entity.center.y - entity.radius);
            updateBounds(entity.center.x + entity.radius, entity.center.y + entity.radius);
          }
          break;
        case 'LWPOLYLINE':
        case 'POLYLINE':
          entity.vertices?.forEach(v => updateBounds(v.x, v.y));
          break;
        case 'POINT':
          updateBounds(entity.position?.x, entity.position?.y);
          break;
        case 'TEXT':
        case 'MTEXT':
          updateBounds(entity.position?.x || entity.startPoint?.x, 
                       entity.position?.y || entity.startPoint?.y);
          break;
        case 'INSERT':
          updateBounds(entity.position?.x, entity.position?.y);
          break;
        default:
          // Try common position properties
          if (entity.position) updateBounds(entity.position.x, entity.position.y);
          if (entity.center) updateBounds(entity.center.x, entity.center.y);
      }
    });
    
    // If no valid bounds found, use defaults
    if (!isFinite(minX)) {
      return { minX: 0, minY: 0, maxX: 100, maxY: 100 };
    }
    
    // Add padding
    const padding = Math.max(maxX - minX, maxY - minY) * 0.1;
    return {
      minX: minX - padding,
      minY: minY - padding,
      maxX: maxX + padding,
      maxY: maxY + padding
    };
  };

  // Fit view to bounds
  const fitToView = useCallback(() => {
    if (!bounds || !containerRef.current) return;
    
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;
    
    const boundsWidth = bounds.maxX - bounds.minX;
    const boundsHeight = bounds.maxY - bounds.minY;
    
    if (boundsWidth <= 0 || boundsHeight <= 0) return;
    
    const scaleX = width / boundsWidth;
    const scaleY = height / boundsHeight;
    const newScale = Math.min(scaleX, scaleY) * 0.9;
    
    setScale(newScale);
    setOffset({
      x: (width - boundsWidth * newScale) / 2 - bounds.minX * newScale,
      y: (height - boundsHeight * newScale) / 2 + bounds.maxY * newScale
    });
  }, [bounds]);

  // Initial fit to view
  useEffect(() => {
    if (dxfData && bounds) {
      setTimeout(fitToView, 100);
    }
  }, [dxfData, bounds, fitToView]);

  // Draw DXF on canvas
  useEffect(() => {
    if (!dxfData || !canvasRef.current || !containerRef.current) return;
    
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    
    // Clear canvas
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Transform coordinates
    const transformX = (x) => x * scale + offset.x;
    const transformY = (y) => -y * scale + offset.y; // Flip Y axis
    
    // Set default style
    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth = 1;
    ctx.fillStyle = '#ffffff';
    ctx.font = '12px monospace';
    
    // Draw entities
    (dxfData.entities || []).forEach(entity => {
      // Skip hidden layers
      if (entity.layer && !visibleLayers.has(entity.layer)) return;
      
      // Get entity color
      const color = getEntityColor(entity, dxfData.tables?.layer);
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      
      try {
        switch (entity.type) {
          case 'LINE':
            if (entity.vertices && entity.vertices.length >= 2) {
              ctx.beginPath();
              ctx.moveTo(transformX(entity.vertices[0].x), transformY(entity.vertices[0].y));
              ctx.lineTo(transformX(entity.vertices[1].x), transformY(entity.vertices[1].y));
              ctx.stroke();
            }
            break;
            
          case 'CIRCLE':
            if (entity.center && entity.radius) {
              ctx.beginPath();
              ctx.arc(
                transformX(entity.center.x),
                transformY(entity.center.y),
                entity.radius * scale,
                0,
                Math.PI * 2
              );
              ctx.stroke();
            }
            break;
            
          case 'ARC':
            if (entity.center && entity.radius) {
              const startAngle = (entity.startAngle || 0) * Math.PI / 180;
              const endAngle = (entity.endAngle || 360) * Math.PI / 180;
              ctx.beginPath();
              ctx.arc(
                transformX(entity.center.x),
                transformY(entity.center.y),
                entity.radius * scale,
                -endAngle, // Flip angles for Y-axis flip
                -startAngle,
                false
              );
              ctx.stroke();
            }
            break;
            
          case 'LWPOLYLINE':
          case 'POLYLINE':
            if (entity.vertices && entity.vertices.length > 0) {
              ctx.beginPath();
              ctx.moveTo(transformX(entity.vertices[0].x), transformY(entity.vertices[0].y));
              for (let i = 1; i < entity.vertices.length; i++) {
                ctx.lineTo(transformX(entity.vertices[i].x), transformY(entity.vertices[i].y));
              }
              if (entity.shape) ctx.closePath();
              ctx.stroke();
            }
            break;
            
          case 'POINT':
            if (entity.position) {
              ctx.beginPath();
              ctx.arc(
                transformX(entity.position.x),
                transformY(entity.position.y),
                3,
                0,
                Math.PI * 2
              );
              ctx.fill();
            }
            break;
            
          case 'TEXT':
          case 'MTEXT':
            const textPos = entity.position || entity.startPoint;
            if (textPos && entity.text) {
              const fontSize = Math.max(8, (entity.height || 10) * scale * 0.5);
              ctx.font = `${fontSize}px monospace`;
              ctx.fillText(
                entity.text,
                transformX(textPos.x),
                transformY(textPos.y)
              );
            }
            break;
            
          case 'SPLINE':
            // Simplified spline rendering using control points
            if (entity.controlPoints && entity.controlPoints.length > 0) {
              ctx.beginPath();
              ctx.moveTo(
                transformX(entity.controlPoints[0].x),
                transformY(entity.controlPoints[0].y)
              );
              for (let i = 1; i < entity.controlPoints.length; i++) {
                ctx.lineTo(
                  transformX(entity.controlPoints[i].x),
                  transformY(entity.controlPoints[i].y)
                );
              }
              ctx.stroke();
            }
            break;
            
          default:
            // Skip unknown entity types
            break;
        }
      } catch (err) {
        console.warn(`Error drawing entity ${entity.type}:`, err);
      }
    });
    
    // Draw scale indicator
    ctx.strokeStyle = '#666';
    ctx.fillStyle = '#888';
    ctx.font = '10px sans-serif';
    const scaleBarLength = 100;
    const realLength = scaleBarLength / scale;
    ctx.beginPath();
    ctx.moveTo(20, canvas.height - 30);
    ctx.lineTo(20 + scaleBarLength, canvas.height - 30);
    ctx.stroke();
    ctx.fillText(`${realLength.toFixed(1)} Einheiten`, 20, canvas.height - 15);
    
  }, [dxfData, scale, offset, visibleLayers]);

  // Get color for entity
  const getEntityColor = (entity, layerTable) => {
    // DXF color codes (simplified)
    const dxfColors = {
      1: '#ff0000', // Red
      2: '#ffff00', // Yellow
      3: '#00ff00', // Green
      4: '#00ffff', // Cyan
      5: '#0000ff', // Blue
      6: '#ff00ff', // Magenta
      7: '#ffffff', // White
      256: '#00ff88' // ByLayer - default green
    };
    
    if (entity.color && dxfColors[entity.color]) {
      return dxfColors[entity.color];
    }
    
    return '#00ff88'; // Default color
  };

  // Mouse/Touch handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setLastPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const dx = e.clientX - lastPos.x;
    const dy = e.clientY - lastPos.y;
    
    setOffset(prev => ({
      x: prev.x + dx,
      y: prev.y + dy
    }));
    
    setLastPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    setScale(prev => {
      const newScale = Math.max(0.01, Math.min(100, prev * delta));
      
      // Zoom towards mouse position
      setOffset(prevOffset => ({
        x: mouseX - (mouseX - prevOffset.x) * (newScale / prev),
        y: mouseY - (mouseY - prevOffset.y) * (newScale / prev)
      }));
      
      return newScale;
    });
  };

  // Touch handlers with Pinch-to-Zoom support
  const initialPinchDistance = useRef(0);
  const initialPinchScale = useRef(1);
  const pinchCenterRef = useRef({ x: 0, y: 0 });
  const initialOffsetRef = useRef({ x: 0, y: 0 });
  
  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getMidpoint = (touches) => {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    };
  };
  
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      setLastPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else if (e.touches.length === 2) {
      // Start pinch-to-zoom - store initial state
      e.preventDefault();
      setIsDragging(false);
      initialPinchDistance.current = getDistance(e.touches);
      initialPinchScale.current = scale;
      
      // Store the pinch center for zoom-to-point
      const midpoint = getMidpoint(e.touches);
      pinchCenterRef.current = midpoint;
      initialOffsetRef.current = { ...offset };
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      // Pinch-to-zoom with center point
      e.preventDefault();
      const currentDistance = getDistance(e.touches);
      const ratio = currentDistance / initialPinchDistance.current;
      const newScale = Math.min(Math.max(initialPinchScale.current * ratio, 0.1), 100);
      
      // Get current pinch center
      const currentMidpoint = getMidpoint(e.touches);
      
      // Calculate the offset adjustment to keep pinch center fixed
      const scaleChange = newScale / initialPinchScale.current;
      
      // The pinch center in world coordinates should stay at the same screen position
      const pinchWorldX = (pinchCenterRef.current.x - initialOffsetRef.current.x) / initialPinchScale.current;
      const pinchWorldY = (pinchCenterRef.current.y - initialOffsetRef.current.y) / initialPinchScale.current;
      
      // Calculate new offset so the world point under the original pinch center 
      // is now under the current pinch center
      const newOffsetX = currentMidpoint.x - pinchWorldX * newScale;
      const newOffsetY = currentMidpoint.y - pinchWorldY * newScale;
      
      setScale(newScale);
      setOffset({ x: newOffsetX, y: newOffsetY });
    } else if (isDragging && e.touches.length === 1) {
      // Pan
      const dx = e.touches[0].clientX - lastPos.x;
      const dy = e.touches[0].clientY - lastPos.y;
      
      setOffset(prev => ({
        x: prev.x + dx,
        y: prev.y + dy
      }));
      
      setLastPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    initialPinchDistance.current = 0;
  };

  // Toggle layer visibility
  const toggleLayer = (layerName) => {
    setVisibleLayers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(layerName)) {
        newSet.delete(layerName);
      } else {
        newSet.add(layerName);
      }
      return newSet;
    });
  };

  // Zoom controls
  const zoomIn = () => setScale(prev => Math.min(100, prev * 1.3));
  const zoomOut = () => setScale(prev => Math.max(0.01, prev * 0.7));

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 size={48} className="animate-spin mx-auto mb-4" />
          <p>DXF-Datei wird geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-xl p-6 max-w-md text-center">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h3 className="text-white text-xl font-bold mb-2">{t('common.loadError', 'Fehler beim Laden')}</h3>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg"
          >
            Schließen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-700 p-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800"
          >
            <X size={24} />
          </button>
          <div>
            <h2 className="text-white font-medium truncate max-w-[200px] sm:max-w-none">
              {fileName || 'DXF Viewer'}
            </h2>
            <p className="text-gray-500 text-xs">
              {dxfData?.entities?.length || 0} Elemente • Zoom: {(scale * 100).toFixed(0)}%
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowLayers(!showLayers)}
            className={`p-2 rounded-lg ${showLayers ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
            title={t('dxf.showLayers', 'Layer anzeigen')}
          >
            <Layers size={20} />
          </button>
          <button
            onClick={fitToView}
            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800"
            title="Einpassen"
          >
            <Maximize2 size={20} />
          </button>
        </div>
      </div>

      {/* Main viewer area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas container */}
        <div 
          ref={containerRef}
          className="flex-1 relative cursor-move"
          style={{ touchAction: 'none' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <canvas 
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
          />
        </div>

        {/* Layer panel */}
        {showLayers && layers.length > 0 && (
          <div className="w-64 bg-gray-900 border-l border-gray-700 p-3 overflow-y-auto">
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <Layers size={18} />
              Layer ({layers.length})
            </h3>
            <div className="space-y-1">
              {layers.map(layer => (
                <button
                  key={layer}
                  onClick={() => toggleLayer(layer)}
                  className={`w-full px-3 py-2 rounded-lg text-left flex items-center gap-2 text-sm
                             ${visibleLayers.has(layer) 
                               ? 'bg-gray-800 text-white' 
                               : 'bg-gray-900 text-gray-500'}`}
                >
                  {visibleLayers.has(layer) ? (
                    <Eye size={16} className="text-emerald-400 shrink-0" />
                  ) : (
                    <EyeOff size={16} className="shrink-0" />
                  )}
                  <span className="truncate">{layer || '(default)'}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Zoom controls */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-2">
        <button
          onClick={zoomIn}
          className="p-3 bg-gray-800 text-white rounded-lg shadow-lg hover:bg-gray-700"
          title={t('common.zoomIn', 'Vergrößern')}
        >
          <ZoomIn size={24} />
        </button>
        <button
          onClick={zoomOut}
          className="p-3 bg-gray-800 text-white rounded-lg shadow-lg hover:bg-gray-700"
          title="Verkleinern"
        >
          <ZoomOut size={24} />
        </button>
        <button
          onClick={fitToView}
          className="p-3 bg-gray-800 text-white rounded-lg shadow-lg hover:bg-gray-700"
          title="Einpassen"
        >
          <RotateCcw size={24} />
        </button>
      </div>

      {/* Instructions */}
      <div className="absolute bottom-4 left-4 text-gray-500 text-xs">
        <p>🖱️ Ziehen zum Verschieben • Scrollen zum Zoomen</p>
      </div>
    </div>
  );
};

export default DxfViewer;
